import React, { useCallback, useState } from "react";
import { dailyApi } from "@/features/daily/api/daily.api";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import { toDailyError } from "@/features/daily/hooks/use-daily-resource";

type Props = {
    /** Enmienda C14: the SAME predicates the table is showing, built by one function. */
    params: Readonly<Record<string, string>>;
    search: string;
    fileName: string;
};

const FORBIDDEN_COPY = "Tu cuenta no puede exportar esta cola.";
const TRUNCATED_COPY =
    "La fuente recortó la exportación: no contiene todas las filas de esta cola. Acota el alcance o la ventana y vuelve a exportar.";
const NO_DOWNLOAD_COPY = "Este navegador no pudo iniciar la descarga.";

export const ExportButton: React.FC<Props> = ({ params, search, fileName }) => {
    const [pending, setPending] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const run = useCallback(async (): Promise<void> => {
        setPending(true);
        setNotice(null);
        setError(null);
        try {
            const result = await dailyApi.exportQueue(params, { search: search === "" ? undefined : search });
            // L350: the notice is the server's own statement about the file it built.
            // Nothing here infers truncation from a row count.
            if (result.truncated) setNotice(TRUNCATED_COPY);
            if (typeof URL.createObjectURL !== "function") {
                setError(NO_DOWNLOAD_COPY);
                return;
            }
            const href = URL.createObjectURL(result.blob);
            const anchor = document.createElement("a");
            anchor.href = href;
            anchor.download = fileName;
            anchor.click();
            URL.revokeObjectURL(href);
        } catch (caught) {
            const failure = toDailyError(caught);
            // FR-026: the server is the authority on who may export. A refusal is
            // reported as a refusal, never retried into a partial file.
            setError(failure.code === "forbidden" ? FORBIDDEN_COPY : failure.message);
        } finally {
            setPending(false);
        }
    }, [params, search, fileName]);

    return (
        <div className="daily-actions">
            <button
                type="button"
                className="daily-button"
                onClick={() => { void run(); }}
                disabled={pending}
                aria-busy={pending}
                aria-label={pending ? "Preparando exportación…" : "Exportar CSV"}
            >
                <DailyIcon name="download" /> CSV
            </button>
            {notice === null ? null : <p className="daily-notice" role="status">{notice}</p>}
            {error === null ? null : <p className="daily-search__error" role="alert">{error}</p>}
        </div>
    );
};
